'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { Store, Users, ArrowRight } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface Plan {
  name: string;
}

interface HowItWorksSectionProps {
  plan: Plan;
}

export function HowItWorksSection({ plan }: HowItWorksSectionProps) {
  const t = useTranslations('planDetails.spilloverBinaryPlan.howItWorks');
  const sectionRef = useScrollAnimation({ threshold: 0.15 });

  const paragraphs = [
    {
      icon: Store,
      text: t('paragraph1'),
    },
    {
      icon: Users,
      text: t('paragraph2'),
    },
  ];

  return (
    <SectionWrapper ref={sectionRef.ref} variant="default" padding="lg" className="relative bg-gray-950 dark:bg-black">
      {/* Subtle gradient background accents */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-purple-500/10 blur-3xl" />
      </div>

      <div className="container relative z-10">
        <div className={cn(
          "text-center mb-16 transition-all duration-700",
          sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        )}>
          <Typography variant="h2" className="mb-6 text-white text-4xl md:text-5xl font-bold">
            {t('title')}
          </Typography>
          {t('description') && (
            <Typography variant="p" className="text-lg text-white/90 max-w-4xl mx-auto">
              {t('description')}
            </Typography>
          )}
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 relative">
            {paragraphs.map((para, index) => {
              const Icon = para.icon;
              const isLast = index === paragraphs.length - 1;

              return (
                <div 
                  key={index} 
                  className={cn(
                    "relative transition-all duration-700",
                    sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  )}
                  style={{ transitionDelay: `${200 + index * 150}ms` }}
                >
                  {/* Vibrant Blue Square Icon Badge - overlapping top-left */}
                  <div className="absolute -top-4 -left-4 w-16 h-16 rounded-xl bg-blue-500 flex items-center justify-center shadow-2xl z-20">
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  {/* Card */}
                  <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 pt-12 border border-blue-800/50 dark:border-blue-700/50 overflow-hidden">
                    {/* Content - Dark text, left-aligned */}
                    <div className="relative z-10">
                      <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed text-base md:text-lg text-left">
                        {para.text}
                      </Typography>
                    </div>
                  </div>

                  {/* Light Blue Arrow connector between cards */}
                  {!isLast && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                      <div className="w-10 h-10 rounded-full bg-blue-400/80 dark:bg-blue-500 flex items-center justify-center shadow-lg">
                        <ArrowRight className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}

